import { useState, useEffect } from 'react';
import { X, MessageSquare, Send } from 'lucide-react';
import { App } from 'antd';
import { auth } from '../services/auth';
import { API_BASE_URL } from '../config';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

type FeedbackType = 'suggestion' | 'bug' | 'other';

const feedbackTypeOptions = [
  { label: '功能建议', value: 'suggestion', emoji: '💡' },
  { label: '问题反馈', value: 'bug', emoji: '🐛' },
  { label: '其他', value: 'other', emoji: '💬' },
];

export default function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const { message } = App.useApp();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('suggestion');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({ content: false });

  // 阻止背景页面滚动
  useEffect(() => {
    if (open) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [open]);

  // 校验反馈内容
  const validateContent = (value: string): string => {
    if (!value.trim()) return '请输入反馈内容';
    return '';
  };

  const contentError = touched.content ? validateContent(content) : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 标记为已触碰以触发校验显示
    setTouched({ content: true });

    // 校验内容
    const error = validateContent(content);
    if (error) {
      message.error(error);
      return;
    }

    setSubmitting(true);

    try {
      const token = auth.getToken();
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: feedbackType,
          content: content.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '提交失败');
      }

      message.success('反馈成功');
      handleClose();
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : '提交失败，请稍后重试',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedbackType('suggestion');
    setContent('');
    setTouched({ content: false });
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
      />

      {/* 弹窗 */}
      <div className="relative w-full max-w-md bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden animate-scale-in">
        {/* 顶部装饰 */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-emerald-500 to-amber-500" />

        {/* 关闭按钮 */}
        <button
          onClick={handleClose}
          disabled={submitting}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {/* 标题 */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MessageSquare className="w-6 h-6 text-amber-400" />
              <h2 className="text-2xl font-bold text-white">产品反馈</h2>
            </div>
            <p className="text-slate-400 text-sm">
              欢迎提任何意见，接受任何建议和批评
            </p>
          </div>

          {/* 表单 */}
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* 反馈类型选择 */}
            <div>
              <label className="block text-slate-300 text-sm mb-3">
                反馈类型
              </label>
              <div className="grid grid-cols-3 gap-3">
                {feedbackTypeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setFeedbackType(option.value as FeedbackType)
                    }
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all duration-200 cursor-pointer
                      ${
                        feedbackType === option.value
                          ? 'bg-amber-500/30 border-amber-500 text-amber-400'
                          : 'bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                      }`}
                  >
                    <span className="mr-1">{option.emoji}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 反馈内容 */}
            <div>
              <label className="block text-slate-300 text-sm mb-2">
                反馈内容 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onBlur={() => setTouched({ content: true })}
                  placeholder="请输入建议或遇到的问题..."
                  rows={4}
                  className={`w-full px-4 py-3 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 
                    focus:outline-none transition-all duration-200 resize-none cursor-text
                    ${
                      contentError
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border-slate-600 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20'
                    }`}
                />
              </div>
              {/* 错误提示 */}
              {contentError && (
                <p className="mt-1.5 text-red-400 text-xs flex items-center gap-1 animate-fade-in">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {contentError}
                </p>
              )}
            </div>

            {/* 提交按钮 */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-semibold 
                  rounded-lg transition-all duration-200 cursor-pointer"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={submitting || !!contentError || !content.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-amber-600 
                  hover:from-amber-400 hover:to-amber-500 text-white font-semibold rounded-lg 
                  transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                  shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 cursor-pointer
                  flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    提交中
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    提交反馈
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 动画样式 */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
